<template>
  <!-- Defence in depth: the model already filters by access at index time, but
       we re-check here so any mid-session permission change (e.g. plan
       downgrade) immediately hides stale results. -->
  <nuxt-link
    v-if="canAccess(result.data.pagePath)"
    :to="result.path"
    class="flex items-center leading-none py-1 text-gray-800 dark:text-gray-100"
  >
    <!-- Manifest icons are stored as raw `lucide:*` names — `OIcon` runs them
         through a friendly-name dictionary first and falls back to a smiley
         when there's no match, so we render them through `UIcon` directly. -->
    <UIcon
      v-if="result.data.pageIcon"
      :name="result.data.pageIcon"
      class="size-4 mr-4 text-gray-500 dark:text-gray-400 flex-shrink-0"
    />
    <div class="min-w-0">
      <!-- Field hits show `Page › Section` as the breadcrumb on top and the
           field label below. Section hits show just the page title. Page hits
           show nothing above. -->
      <p
        v-if="breadcrumb"
        class="text-xs text-gray-500 dark:text-gray-400 mb-0.5 truncate"
      >
        {{ breadcrumb }}
      </p>
      <p class="my-1 truncate">
        {{ $t(primaryLabel) }}
      </p>
      <p v-if="result.data.fieldInfo" class="text-gray-600 text-sm dark:text-gray-300 truncate">
        {{ $t(result.data.fieldInfo) }}
      </p>
    </div>
  </nuxt-link>
</template>

<script setup lang="ts">
interface Props {
  result: {
    path: string
    data: {
      pagePath: string
      pageTitle: string
      pageIcon?: string
      sectionLabel?: string
      fieldLabel?: string
      fieldInfo?: string
      kind: 'page' | 'section' | 'field'
    }
  }
}

const props = defineProps<Props>()
const { t } = useI18n()
const canAccess = useAccess()

// What goes in the main row of the card — most-specific label wins.
const primaryLabel = computed(() => {
  if (props.result.data.kind === 'field' && props.result.data.fieldLabel) return props.result.data.fieldLabel
  if (props.result.data.kind === 'section' && props.result.data.sectionLabel) return props.result.data.sectionLabel
  return props.result.data.pageTitle
})

// Breadcrumb shown above the primary label. `Page › Section` for field hits;
// just `Page` for section hits; nothing for page hits.
const breadcrumb = computed(() => {
  const d = props.result.data
  if (d.kind === 'page') return ''
  if (d.kind === 'section') return t(d.pageTitle)
  // field
  const parts = [t(d.pageTitle)]
  if (d.sectionLabel) parts.push(t(d.sectionLabel))
  return parts.join(' › ')
})
</script>
